import * as Sentry from '@sentry/react';
import type { Breadcrumb } from '@sentry/react';
import { makeBrowserOfflineTransport, makeFetchTransport } from '@sentry/react';
import type { ChatErrorReporter } from '@/types/errorReporter';
import { getTabCoordination, TabInfo } from '@/utils/helpers/tabCoordination';
import {
  extractDigitUid,
  extractInternalIdFromUserCookie
} from '@/utils/helpers/userIdentification';
import {
  isTokenDiagnosticEvent,
  reportTokenRequestDiagnostic,
  sanitizeTokenDiagnosticEvent
} from './turboSentryTokenDiagnosticReporter';

const CHAT_DOMAIN_TAG = 'chat';

export const sanitizeChatBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  if (breadcrumb.category !== 'console' || !breadcrumb.data) {
    return breadcrumb;
  }

  const safeData = { ...breadcrumb.data };
  delete safeData.arguments;

  return {
    ...breadcrumb,
    data: safeData
  };
};

let lastVisibilityChangeTime = Date.now();
let timeWhenBecameHidden = 0;

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    lastVisibilityChangeTime = Date.now();
    if (document.visibilityState === 'hidden') {
      timeWhenBecameHidden = Date.now();
    }
  });
}

const getTabCoordinationInfo = async (): Promise<{
  tabInfo: TabInfo | null;
  error: string | null;
}> => {
  try {
    const tabCoordination = getTabCoordination();
    const tabInfo = await tabCoordination.getTabInfo();
    return { tabInfo, error: null };
  } catch (e) {
    return {
      tabInfo: null,
      error: `Failed to get tab coordination info: ${e instanceof Error ? e.message : String(e)}`
    };
  }
};

if (typeof window !== 'undefined') {
  try {
    const dsn = process.env.SENTRY_DSN;
    if (!dsn) {
      console.warn('[Sentry] DSN not configured, skipping initialization');
    } else {
      const isProduction = window.location.hostname !== 'localhost';

      Sentry.init({
        dsn,
        environment: process.env.NODE_ENV,
        release: process.env.CHAT_SENTRY_RELEASE,
        debug: !isProduction,
        normalizeDepth: 6,
        transport: makeBrowserOfflineTransport(makeFetchTransport),
        integrations: [
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false
          })
        ],
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: isProduction ? 0.1 : 1.0,
        beforeSend(event) {
          if (event.tags?.domain !== CHAT_DOMAIN_TAG) {
            return null;
          }
          return isTokenDiagnosticEvent(event) ? sanitizeTokenDiagnosticEvent(event) : event;
        },
        beforeBreadcrumb(breadcrumb) {
          return sanitizeChatBreadcrumb(breadcrumb);
        }
      });
    }
  } catch (e) {
    const errorDetails = {
      message: e instanceof Error ? e.message : String(e),
      name: e instanceof Error ? e.name : typeof e
    };
    console.error('Failed to initialize Sentry:', errorDetails);
  }
}

const captureException = async (error: Error, context?: Record<string, unknown>) => {
  try {
    if (typeof window === 'undefined') return;

    (error as Error & { chatWebHandled: boolean }).chatWebHandled = true;

    Sentry.addBreadcrumb({
      category: 'graphql',
      message: 'GraphQL Error',
      data: {
        errorMessage: error.message,
        errorName: error.name,
        timestamp: new Date().toISOString()
      },
      level: 'error'
    });

    const { digitUid, error: extractionError } = extractDigitUid(context);
    const { tabInfo, error: tabCoordinationError } = await getTabCoordinationInfo();

    const isBackgroundTab = document.visibilityState === 'hidden';
    const timeInBackground =
      isBackgroundTab && timeWhenBecameHidden > 0 ? Date.now() - timeWhenBecameHidden : 0;

    const internalId = extractInternalIdFromUserCookie();

    if (digitUid || internalId) {
      Sentry.setUser({
        id: digitUid || internalId || undefined,
        ...(digitUid && { digitUid }),
        ...(internalId && { internalId })
      });
    }

    Sentry.withScope(scope => {
      scope.setTag('domain', CHAT_DOMAIN_TAG);
      if (process.env.CHAT_SENTRY_RELEASE) {
        scope.setTag('chatRelease', process.env.CHAT_SENTRY_RELEASE);
        scope.setContext('chatRelease', {
          release: process.env.CHAT_SENTRY_RELEASE
        });
      }

      if (context) {
        const filteredContext = Object.keys(context).reduce(
          (acc, key) => {
            if (key !== 'user' && key !== 'digitUid') {
              acc[key] = context[key];
            }
            return acc;
          },
          {} as Record<string, unknown>
        );

        scope.setContext('request', filteredContext);
        scope.setContext('tabVisibility', {
          isBackgroundTab,
          visibilityState: document.visibilityState,
          timeInBackground,
          lastVisibilityChangeTime
        });

        if (tabInfo) {
          scope.setContext('tabCoordination', {
            openedTabsIds: tabInfo.openedTabsIds,
            tabCount: tabInfo.tabCount,
            tabIsLeader: tabInfo.isLeader,
            tabLeaderId: tabInfo.leaderId
          });
        }

        if (extractionError) {
          scope.setTag('digitUidExtractionError', 'true');
          scope.setExtra('digitUidExtractionError', extractionError);
        }

        if (tabCoordinationError) {
          scope.setTag('tabCoordinationError', 'true');
          scope.setExtra('tabCoordinationError', tabCoordinationError);
        }

        scope.setExtra('timestamp', new Date().toISOString());
      }

      Sentry.captureException(error);
    });
  } catch (notifyErr) {
    console.error('Failed to report chat error to Sentry:', notifyErr);
  }
};

export const turboSentryErrorReporter: ChatErrorReporter = {
  captureException,
  addBreadcrumb: breadcrumb => Sentry.addBreadcrumb(breadcrumb),
  captureTokenRequestDiagnostic: reportTokenRequestDiagnostic
};
